export type ModuleName = "cmm" | "vms" | "agent" | string;

export type ModulePermissions = Record<string, unknown>;

const KNOWN_MODULES: ModuleName[] = ["cmm", "vms", "agent"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function toBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  return fallback;
}

function normalizeModuleEntry(moduleValue: unknown): Record<string, unknown> | boolean {
  if (typeof moduleValue === "boolean") {
    return moduleValue;
  }
  if (isRecord(moduleValue)) {
    return moduleValue;
  }
  return true;
}

export function getDefaultModulePermissions(): ModulePermissions {
  return {};
}

export function normalizeModulePermissions(input: unknown): ModulePermissions {
  if (!isRecord(input)) {
    return getDefaultModulePermissions();
  }

  const container = isRecord(input.modules) ? input.modules : input;
  const normalized: ModulePermissions = {};

  for (const moduleKey of KNOWN_MODULES) {
    if (moduleKey in container) {
      normalized[moduleKey] = normalizeModuleEntry(container[moduleKey]);
    }
  }

  if (Object.keys(normalized).length > 0) {
    return normalized;
  }

  return { ...container };
}

export function canAccessModule(
  permissions: ModulePermissions | null | undefined,
  moduleName?: ModuleName,
): boolean {
  if (!moduleName) {
    return true;
  }
  if (!permissions || Object.keys(permissions).length === 0) {
    return true;
  }

  const moduleValue = permissions[moduleName];
  if (moduleValue === undefined) {
    return true;
  }
  if (typeof moduleValue === "boolean") {
    return moduleValue;
  }
  if (!isRecord(moduleValue)) {
    return true;
  }

  return toBoolean(moduleValue.enabled, true);
}

export function canAccessFeature(
  permissions: ModulePermissions | null | undefined,
  moduleName?: ModuleName,
  featureName?: string,
): boolean {
  if (!moduleName) {
    return true;
  }
  if (!permissions || Object.keys(permissions).length === 0) {
    return true;
  }

  const moduleValue = permissions[moduleName];
  if (moduleValue === undefined) {
    return true;
  }
  if (typeof moduleValue === "boolean") {
    return moduleValue;
  }
  if (!isRecord(moduleValue)) {
    return true;
  }

  const moduleEnabled = toBoolean(moduleValue.enabled, true);
  if (!moduleEnabled) {
    return false;
  }
  if (!featureName) {
    return true;
  }

  const directFeatureValue = moduleValue[featureName];
  if (typeof directFeatureValue === "boolean") {
    return directFeatureValue;
  }
  if (isRecord(directFeatureValue)) {
    return toBoolean(directFeatureValue.enabled, true);
  }

  const nestedFeatures = moduleValue.features;
  if (isRecord(nestedFeatures)) {
    const nestedFeatureValue = nestedFeatures[featureName];
    if (typeof nestedFeatureValue === "boolean") {
      return nestedFeatureValue;
    }
    if (isRecord(nestedFeatureValue)) {
      return toBoolean(nestedFeatureValue.enabled, true);
    }
  }

  return true;
}
